import React, { useState, useEffect, useRef } from "react";

/**
 * LazyImage - Performance-optimized image component with lazy loading
 *
 * Loads images only when they enter the viewport using Intersection Observer API.
 * This reduces initial page load time, saves bandwidth, and improves performance.
 *
 * @example
 * <LazyImage
 *   src="/path/to/image.jpg"
 *   alt="Image description"
 *   placeholder="/path/to/placeholder.jpg"
 *   className="optional-class"
 * />
 */

interface LazyImageProps {
  src: string;
  alt: string;
  className?: string;
  placeholder: string;
}

export default function LazyImage({
  src,
  alt,
  className,
  placeholder,
}: LazyImageProps) {
  const [imageSrc, setImageSrc] = useState(placeholder);
  const [isLoaded, setIsLoaded] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const currentImg = imgRef.current;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !isLoaded) {
            setImageSrc(src);
            setIsLoaded(true);
          }
        });
      },
      {
        rootMargin: "50px", // Start loading 50px before the image enters viewport
        threshold: 0.01,
      },
    );

    if (currentImg) {
      observer.observe(currentImg);
    }

    return () => {
      if (currentImg) {
        observer.unobserve(currentImg);
      }
    };
  }, [src, isLoaded]);

  return <img ref={imgRef} className={className} src={imageSrc} alt={alt} />;
}
